import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/button';
import {
  AppLayout,
  PageBackLink
} from '../../../components/layout';

import {
  OTRStatusBadge,
  OTRFilterBar,
  OTRPagination
} from '../components';

import {
  getTravelRequestsPage
} from '../../../services/outstationTravelService';

import { OTR_STATUS, getModeOfTravelName } from '../constants/otrEnums';
import { errMsg } from '../utils/errorMessage';

import { formatOtrDate } from '../utils/formatOtrDate';

export const OTRListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);

  const PAGE_SIZE = 10;

  const [pageInfo, setPageInfo] = useState({
    totalPages: 1,
    totalElements: 0
  });


  /*
   * Statuses received from the backend.
   *
   * IMPORTANT:
   * These are NOT taken from OTR_STATUS.
   * The backend response is the source of truth.
   */
  // Seeded with every known status so the dropdown is complete from the start
  // (and doesn't shrink to one option after filtering); statuses returned by
  // the backend are merged in below.
  const [error, setError] = useState('');
  const [statusOptions, setStatusOptions] = useState(Object.values(OTR_STATUS));

  // A new filter means a new result set: go back to the first page.
  const handleFilterChange = (next) => {
    setFilters(next);
    setPage(0);
  };

  useEffect(() => {
    let active = true;

    const loadRequests = async () => {
      setLoading(true);
      setError('');

      try {
        const data = await getTravelRequestsPage(
          {
            ...filters,
            page,
            size: PAGE_SIZE
          },
          'self'
        );

        if (!active) return;

        const content = Array.isArray(data?.content)
          ? data.content
          : [];

        setRequests(content);

        /*
         * Extract the EXACT status values returned by the backend.
         *
         * Example:
         *
         * backend:
         * {
         *   status: "Request_Submitted"
         * }
         *
         * dropdown:
         * Request_Submitted
         *
         * filter request:
         * ?status=Request_Submitted
         */
        const backendStatuses = content
          .map((request) => request?.status)
          .filter(
            (status) =>
              status !== null &&
              status !== undefined &&
              String(status).trim() !== ''
          );

        /*
         * Keep previously discovered statuses as well.
         *
         * This prevents statuses from disappearing when moving
         * between pagination pages.
         */
        setStatusOptions((previous) => {
          return [
            ...new Set([
              ...previous,
              ...backendStatuses
            ])
          ];
        });

        setPageInfo({
          totalPages: data?.totalPages ?? 1,
          totalElements: data?.totalElements ?? content.length
        });

      } catch (err) {
        console.error(
          'Failed to load travel requests',
          err
        );

        if (active) {
          setError(errMsg(err, 'Failed to load travel requests.'));
          setRequests([]);

          setPageInfo({
            totalPages: 1,
            totalElements: 0
          });
        }

      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      active = false;
    };
  }, [filters, page]);

  return (
    <AppLayout>
      <PageBackLink to="/" />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-6 sm:mb-8">

        <div>
          <h1 className="text-[28px] font-bold text-foreground leading-tight">
            {t('outstation.title')}
          </h1>

          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {t('outstation.manageRequests')}
          </p>
        </div>

        <Button
          onClick={() =>
            navigate('/outstation-travel/create')
          }
          className="w-full sm:w-auto h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]"
        >
          {t('outstation.newRequest')}
        </Button>

      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 text-sm text-[var(--color-error)] font-medium">
          {error}
        </div>
      )}

      <OTRFilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        statusOptions={statusOptions}
      />

      {loading ? (

        <div className="py-20 text-center text-[var(--color-text-secondary)] font-medium italic">
          Loading requests...
        </div>

      ) : (

        <div className="bg-white rounded-2xl sm:rounded-[2rem] border border-[var(--color-border)] shadow-sm overflow-hidden">

          <table className="max-md:block w-full text-left border-collapse">

            <thead className="max-md:hidden">
              <tr className="bg-[var(--color-workspace)]/30 border-b border-[var(--color-border)]">

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.reference')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.destination')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.travelDates')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.purpose')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.modeOfTravel')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t('outstation.status')}
                </th>

                <th className="px-6 py-4 text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">
                  {t('outstation.actions')}
                </th>

              </tr>
            </thead>

            <tbody className="max-md:block divide-y divide-[var(--color-border)]">

              {requests.map((req) => (

                <tr
                  key={req.travelRequestId}
                  className="max-md:block max-md:px-4 max-md:py-3 hover:bg-[var(--color-workspace)]/10 transition-colors cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/outstation-travel/${req.travelRequestId}`
                    )
                  }
                >

                  <td data-label="Reference" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm font-bold text-[var(--color-primary)]">
                    {req.requestRef ||
                      `OTR-${req.travelRequestId}`}
                  </td>

                  <td data-label="Destination" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm font-medium text-foreground">
                    {req.placeOfTravel || '—'}
                  </td>

                  <td data-label="Travel Dates" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {formatOtrDate(req.dateOfTravel)}
                    {` ${t('outstation.dateRangeTo')} `}
                    {formatOtrDate(req.dateOfReturn)}
                  </td>

                  <td data-label={t('outstation.purpose')} className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {req.purpose || '—'}
                  </td>

                  <td data-label="Mode" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-sm text-[var(--color-text-secondary)]">
                    {getModeOfTravelName(req)}
                  </td>

                  <td data-label="Status" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5">
                    <OTRStatusBadge
                      status={req.status}
                    />
                  </td>

                  <td data-label="Actions" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-6 py-5 text-right">

                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();

                        navigate(
                          `/outstation-travel/${req.travelRequestId}`
                        );
                      }}
                      className="text-[var(--color-primary)] font-bold text-[12px] uppercase tracking-wider hover:underline"
                    >
                      View
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

          {requests.length === 0 && (

            <div className="py-20 text-center text-[var(--color-text-secondary)] italic">
              {filters.status
                ? 'No requests match the selected status.'
                : 'No travel requests found.'}
            </div>

          )}

          <OTRPagination
            page={page}
            totalPages={pageInfo.totalPages}
            totalElements={pageInfo.totalElements}
            onPageChange={setPage}
            disabled={loading}
          />

        </div>

      )}

    </AppLayout>
  );
};